import { Box, VStack, Select, Button, FormControl, FormLabel, useToast, Collapse, HStack, Input, Modal, ModalOverlay, ModalContent, ModalBody, useDisclosure, Switch, Text, ScaleFade, IconButton, Tooltip, Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody } from '@chakra-ui/react'
import { InfoIcon } from '@chakra-ui/icons'
import { useState, useEffect } from 'react'
import { createMatch } from '@/services/firebase'
import { MatchData, TeamColor, ScoringStructure } from '@/types/match'
import { FaArrowLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'
import { useAuthContext } from '@/contexts/AuthContext'
import { usePaywallContext } from '@/contexts/PaywallContext'
import { FaSync } from 'react-icons/fa'
import { adminService } from '@/services/firestore'
import { auth } from '@/config/firebase'
import { CourtSelectionModal } from './CourtSelectionModal'

interface MatchSetupProps {
  onComplete: (data: MatchData) => void
  onBackToLanding?: () => void
}

export function MatchSetup({ onComplete, onBackToLanding }: MatchSetupProps) {
  
  const [scoringStructure, setScoringStructure] = useState<ScoringStructure>(() => {
    const savedPreference = localStorage.getItem('scoringStructurePreference')
    return (savedPreference as ScoringStructure) || 'rally'
  })
  const [team1Color, setTeam1Color] = useState<TeamColor>('yellow')
  const [moreColorsEnabled, setMoreColorsEnabled] = useState(false)
  const [team2Color, setTeam2Color] = useState<TeamColor>(() => {
    const saved = localStorage.getItem('team2Color');
    return (saved as TeamColor) || 'orange';
  })
  const [team1Name, setTeam1Name] = useState('')
  const [team2Name, setTeam2Name] = useState('')
  const [bestOfSets, setBestOfSets] = useState(3)
  const [pointsToWin, setPointsToWin] = useState(() => {
    const saved = localStorage.getItem('rallyPointsToWin')
    return saved ? parseInt(saved, 10) : 11
  })
  const [winByTwo, setWinByTwo] = useState<boolean | null>(() => {
    const savedScoringStructure = localStorage.getItem('scoringStructurePreference') as ScoringStructure | null
    
    if (savedScoringStructure === 'tennis') {
      // Load tennis setting
      const saved = localStorage.getItem('tennisWinByTwo')
      if (saved === 'true') return true
      if (saved === 'false') return false
      return true // default for tennis
    } else {
      // Load rally setting
      const saved = localStorage.getItem('rallyWinByTwo')
      if (saved === 'true') return true
      if (saved === 'false') return false
      if (saved === 'null') return null
      return true // default for rally
    }
  })
  const [gamesPerSet, setGamesPerSet] = useState(() => {
    const saved = localStorage.getItem('tennisGamesPerSet')
    return saved ? parseInt(saved, 10) : 6
  })
  const [tiebreakerType, setTiebreakerType] = useState(() => {
    const saved = localStorage.getItem('tennisTiebreakerType')
    return saved || 'firstTo7'
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showQRCode, setShowQRCode] = useState(() => {
    const savedPreference = localStorage.getItem('showQRCodePreference')
    return savedPreference !== null ? savedPreference === 'true' : true
  })
  const [showStartMatchPopup, setShowStartMatchPopup] = useState(false)
  const [createdMatchData, setCreatedMatchData] = useState<MatchData | null>(null)
  const [skipMicPopup, setSkipMicPopup] = useState(false)
  const { isOpen, onOpen, onClose } = useDisclosure()
  
  // Court selection modal state
  const [showCourtSelection, setShowCourtSelection] = useState(false)
  const [pendingMatchData, setPendingMatchData] = useState<MatchData | null>(null)
  const [selectedCourt, setSelectedCourt] = useState<number | null>(null)
  const toast = useToast()
  const recentMatchId = localStorage.getItem('recentMatchId')
  
  // Clear recentMatchId to prevent auto-navigation
  if (recentMatchId) {
    localStorage.removeItem('recentMatchId')
  }

  const { isAuthenticated, loading } = useAuthContext();
  const { userSubscription } = usePaywallContext();
  

  const [adminSettings, setAdminSettings] = useState<any>(null);
  const [adminLoading, setAdminLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch admin settings (Phase 6)
  useEffect(() => {
    async function fetchSettings() {
      setAdminLoading(true);
      try {
        const settings = await adminService.getAdminSettings();
        setAdminSettings(settings);
      } catch (error) {
        console.error('Error fetching admin settings:', error);
      } finally {
        setAdminLoading(false);
      }
    }
    fetchSettings();
  }, []);

  // Club QR code logic (Phase 6)
  const isIndividualPlan = userSubscription && userSubscription.planType === 'individual';
  const clubQRCodeDisabled = isIndividualPlan && adminSettings && adminSettings.allowIndividualClubQRCode === false;
  const clubQRCodeLoading = !adminSettings;

  useEffect(() => {
    // Show appropriate video
    // onOpen() // Temporarily disabled due to missing video files
  }, [onOpen])

  // Save QR code preference whenever it changes
  useEffect(() => {
    localStorage.setItem('showQRCodePreference', showQRCode.toString())
  }, [showQRCode])

  // Save scoring structure preference whenever it changes
  useEffect(() => {
    localStorage.setItem('scoringStructurePreference', scoringStructure)
  }, [scoringStructure])

  useEffect(() => {
    localStorage.setItem('team2Color', team2Color);
  }, [team2Color]);

  // Save Rally Scoring preferences (only when in rally mode)
  useEffect(() => {
    if (scoringStructure === 'rally') {
      localStorage.setItem('rallyPointsToWin', pointsToWin.toString())
    }
  }, [pointsToWin, scoringStructure])

  useEffect(() => {
    if (scoringStructure === 'rally') {
      localStorage.setItem('rallyWinByTwo', winByTwo === null ? 'null' : winByTwo.toString())
    }
  }, [winByTwo, scoringStructure])

  // Save Tennis Scoring preferences (only when in tennis mode)
  useEffect(() => {
    if (scoringStructure === 'tennis') {
      localStorage.setItem('tennisGamesPerSet', gamesPerSet.toString())
    }
  }, [gamesPerSet, scoringStructure])

  useEffect(() => {
    if (scoringStructure === 'tennis') {
      if (winByTwo === true || winByTwo === false) {
        localStorage.setItem('tennisWinByTwo', winByTwo.toString())
      }
    }
  }, [winByTwo, scoringStructure])

  useEffect(() => {
    if (scoringStructure === 'tennis') {
      localStorage.setItem('tennisTiebreakerType', tiebreakerType)
    }
  }, [tiebreakerType, scoringStructure])

  // Reset settings when switching scoring structures
  useEffect(() => {
    if (scoringStructure === 'tennis') {
      // Restore Tennis preferences from localStorage
      const savedGamesPerSet = localStorage.getItem('tennisGamesPerSet')
      const savedWinByTwo = localStorage.getItem('tennisWinByTwo')
      const savedTiebreakerType = localStorage.getItem('tennisTiebreakerType')
      
      if (savedGamesPerSet) {
        setGamesPerSet(parseInt(savedGamesPerSet, 10))
      }
      if (savedWinByTwo) {
        setWinByTwo(savedWinByTwo === 'true')
      } else {
        // Default to "Win by 2" if no saved preference
        setWinByTwo(true)
      }
      if (savedTiebreakerType) {
        setTiebreakerType(savedTiebreakerType)
      }
    } else if (scoringStructure === 'rally') {
      // Restore Rally preferences from localStorage
      const savedPointsToWin = localStorage.getItem('rallyPointsToWin')
      const savedWinByTwo = localStorage.getItem('rallyWinByTwo')
      
      if (savedPointsToWin) {
        setPointsToWin(parseInt(savedPointsToWin, 10))
      }
      if (savedWinByTwo) {
        if (savedWinByTwo === 'true') setWinByTwo(true)
        else if (savedWinByTwo === 'false') setWinByTwo(false)
        else if (savedWinByTwo === 'null') setWinByTwo(null)
      }
    }
  }, [scoringStructure])

  // Handle team color conflicts - if team1Color changes to match team2Color, reset team2Color
  useEffect(() => {
    if (team1Color === team2Color) {
      // Find the first available color that's not team1Color
      const availableColors: TeamColor[] = ['red', 'yellow', 'green', 'purple', 'blue', 'orange'];
      const newTeam2Color = availableColors.find(color => color !== team1Color);
      if (newTeam2Color) {
        setTeam2Color(newTeam2Color);
      }
    }
  }, [team1Color, team2Color]);

  // Reset colors when more colors is disabled
  useEffect(() => {
    if (!moreColorsEnabled) {
      setTeam1Color('yellow');
      setTeam2Color('orange');
    }
  }, [moreColorsEnabled]);

  useEffect(() => {
    if (localStorage.getItem('skipMicPopup') === 'true') {
      setSkipMicPopup(true)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Ensure user is authenticated before creating a match
    if (!isAuthenticated) {
      navigate('/auth')
      return
    }
    
    setIsLoading(true)

    try {
      // Get user information for court tagging
      const currentUser = auth.currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      // Get user subscription details from context
      const subscriptionDetails = {
        planType: userSubscription?.planType || 'individual' as 'individual' | 'club',
        maxCourts: userSubscription?.courts || 1 // Default to 1 court for individual plans
      }

      // For individual users, automatically assign court 1
      if (subscriptionDetails.planType === 'individual') {
        const courtNumber = 1
        const individualMatchData: MatchData = {
          id: '', // Placeholder, will be set after creation
          team1: team1Name,
          team2: team2Name,
          team1Name: team1Name,
          team2Name: team2Name,
          games: { team1: 0, team2: 0 },
          sets: { team1: 0, team2: 0 },
          currentSet: 1,
          setsPerMatch: bestOfSets,
          pointsPerSet: scoringStructure === 'tennis' ? gamesPerSet : pointsToWin,
          tiebreakEnabled: scoringStructure === 'tennis',
          tiebreakerType,
          pendingSetCompletion: false,
          lastUpdated: Date.now(),
          createdAt: Date.now(),
          status: 'active',
          matchType: scoringStructure === 'tennis' ? 'tennis' : 'regular',
          sport: 'Tennis',
          venue: '',
          date: '',
          time: '',
          scoringStructure,
          team1Color,
          team2Color,
          bestOf: bestOfSets,
          winByTwo: winByTwo === true,
          pointsStructure: winByTwo === true ? 'winByTwo' : winByTwo === false ? 'golden' : 'fixed',
          scores: { team1: 0, team2: 0, updatedAt: Date.now() },
          showQRCode,
          // Court tagging fields
          createdBy: currentUser?.uid || '',
          userEmail: currentUser?.email || '',
          courtNumber: courtNumber,
          planType: subscriptionDetails.planType,
          maxCourts: subscriptionDetails.maxCourts
        }

        // Create match immediately for individual users
        await createMatchAndProceed(individualMatchData)
        return
      }

      // For club users, show court selection modal
      const clubMatchData: MatchData = {
        id: '', // Placeholder, will be set after creation
        team1: team1Name,
        team2: team2Name,
        team1Name: team1Name,
        team2Name: team2Name,
        games: { team1: 0, team2: 0 },
        sets: { team1: 0, team2: 0 },
        currentSet: 1,
        setsPerMatch: bestOfSets,
        pointsPerSet: scoringStructure === 'tennis' ? gamesPerSet : pointsToWin,
        tiebreakEnabled: scoringStructure === 'tennis',
        tiebreakerType,
        pendingSetCompletion: false,
        lastUpdated: Date.now(),
        createdAt: Date.now(),
        status: 'active',
        matchType: scoringStructure === 'tennis' ? 'tennis' : 'regular',
        sport: 'Tennis',
        venue: '',
        date: '',
        time: '',
        scoringStructure,
        team1Color,
        team2Color,
        bestOf: bestOfSets,
        winByTwo: winByTwo === true,
        pointsStructure: winByTwo === true ? 'winByTwo' : winByTwo === false ? 'golden' : 'fixed',
        scores: { team1: 0, team2: 0, updatedAt: Date.now() },
        showQRCode,
        // Court tagging fields (court number will be set when user selects)
        createdBy: currentUser.uid,
        userEmail: currentUser.email || '',
        courtNumber: 0, // Will be set when court is selected
        planType: subscriptionDetails.planType,
        maxCourts: subscriptionDetails.maxCourts
      }

      // Store match data and show court selection modal
      setPendingMatchData(clubMatchData)
      setShowCourtSelection(true)
      setIsLoading(false)
      return
    } catch (error) {
      console.error('Error creating match:', error)
      toast({
        title: 'Error',
        description: 'Failed to create match. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleGotItClick = () => {
    if (createdMatchData) {
      setShowStartMatchPopup(false)
      onComplete(createdMatchData)
    }
  }

  const handleNeverTellMeAgain = () => {
    localStorage.setItem('skipMicPopup', 'true')
    setSkipMicPopup(true)
    if (createdMatchData) {
      setShowStartMatchPopup(false)
      onComplete(createdMatchData)
    }
  }

  const handleResumeMatch = () => {
    console.log('Resume match clicked, recentMatchId:', recentMatchId)
    if (recentMatchId) {
      console.log('Navigating to resume match...')
      window.location.href = `/?matchId=${recentMatchId}&controller=true`
    }
  }

  const handleBackToLanding = () => {
    if (onBackToLanding) {
      onBackToLanding()
    }
  }

  // Function to create match and proceed with the flow
  const createMatchAndProceed = async (matchData: MatchData) => {
    try {
      const { matchId, hostControllerToken } = await createMatch(matchData)
      localStorage.setItem('recentMatchId', matchId)
      localStorage.setItem('controllerToken', hostControllerToken)
      
      const finalMatchData = { ...matchData, id: matchId, controllerToken: hostControllerToken }
      
      // Store the match data and show the popup instead of immediately calling onComplete
      setCreatedMatchData(finalMatchData)
      if (localStorage.getItem('skipMicPopup') === 'true') {
        onComplete(finalMatchData)
      } else {
        setShowStartMatchPopup(true)
        setTimeout(() => {
          if (showStartMatchPopup) {
            setShowStartMatchPopup(false)
            onComplete(finalMatchData)
          }
        }, 7000)
      }
    } catch (error) {
      console.error('Error creating match:', error)
      toast({
        title: 'Error',
        description: 'Failed to create match. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Handle court selection for club users
  const handleCourtSelected = async (courtNumber: number) => {
    if (!pendingMatchData) return

    const matchDataWithCourt = {
      ...pendingMatchData,
      courtNumber: courtNumber
    }

    await createMatchAndProceed(matchDataWithCourt)
    setPendingMatchData(null)
    setSelectedCourt(null)
  }

  return (
    <Box pt={16} px={4} pb={4} minH="100vh" display="flex" flexDirection="column" bg="#09090b" color="white">
      {/* Back Arrow */}
      <Box position="absolute" top={4} left="50%" transform="translateX(-50%)" zIndex={9999}>
        <IconButton
          aria-label="Back to landing page"
          icon={<FaArrowLeft />}
          onClick={handleBackToLanding}
          variant="ghost"
          color="white"
          _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
          size="lg"
        />
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay bg="black" />
        <ModalContent 
          bg="transparent" 
          maxW="100vw" 
          maxH="100vh" 
          m={0} 
          p={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <ModalBody p={0} w="100%" h="100%">
            <video
              src={"/intro-long.mp4"}
              autoPlay
              muted
              playsInline
              style={
                {
                  maxWidth: '100vw',
                  maxHeight: '100vh',
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  margin: '0 auto',
                }
              }
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <VStack spacing={4} as="form" onSubmit={handleSubmit}>
        <FormControl>
          <FormLabel textAlign="center"></FormLabel>
          <HStack justify="center" spacing={2}>
            <Button
              colorScheme={scoringStructure === 'rally' ? 'blue' : 'gray'}
              onClick={() => setScoringStructure('rally')}
              flex={1}
              _hover={{ bg: '#3182ce' }}
              bg={scoringStructure === 'rally' ? '#3182ce' : undefined}
              borderRadius="2px"
              maxW="150px"
            >
              Rally Scoring
            </Button>
            <Button
              colorScheme={scoringStructure === 'tennis' ? 'blue' : 'gray'}
              onClick={() => setScoringStructure('tennis')}
              flex={1}
              _hover={{ bg: '#3182ce' }}
              bg={scoringStructure === 'tennis' ? '#3182ce' : undefined}
              borderRadius="2px"
              maxW="150px"
            >
              Tennis Scoring
            </Button>
          </HStack>
        </FormControl>

        <Collapse in={scoringStructure === 'rally'}>
          <VStack spacing={4} w="100%">
            <FormControl>
              <FormLabel>Best of</FormLabel>
              <HStack spacing={2}>
                <Button
                  colorScheme={bestOfSets === 1 ? 'blue' : 'gray'}
                  onClick={() => setBestOfSets(1)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={bestOfSets === 1 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  1 Set
                </Button>
                <Button
                  colorScheme={bestOfSets === 3 ? 'blue' : 'gray'}
                  onClick={() => setBestOfSets(3)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={bestOfSets === 3 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  3 Sets
                </Button>
                <Button
                  colorScheme={bestOfSets === 5 ? 'blue' : 'gray'}
                  onClick={() => setBestOfSets(5)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={bestOfSets === 5 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  5 Sets
                </Button>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel textAlign="left">First to</FormLabel>
              <VStack spacing={2}>
                <HStack spacing={2} w="100%">
                  <Button
                    colorScheme={pointsToWin === 11 ? 'blue' : 'gray'}
                    onClick={() => setPointsToWin(11)}
                    flex={1}
                    _hover={{ bg: '#3182ce' }}
                    bg={pointsToWin === 11 ? '#3182ce' : undefined}
                    borderRadius="2px"
                  >
                    11 Points
                  </Button>
                  <Button
                    colorScheme={pointsToWin === 15 ? 'blue' : 'gray'}
                    onClick={() => setPointsToWin(15)}
                    flex={1}
                    _hover={{ bg: '#3182ce' }}
                    bg={pointsToWin === 15 ? '#3182ce' : undefined}
                    borderRadius="2px"
                  >
                    15 Points
                  </Button>
                  <Button
                    colorScheme={pointsToWin === 21 ? 'blue' : 'gray'}
                    onClick={() => setPointsToWin(21)}
                    flex={1}
                    _hover={{ bg: '#3182ce' }}
                    bg={pointsToWin === 21 ? '#3182ce' : undefined}
                    borderRadius="2px"
                  >
                    21 Points
                  </Button>
                </HStack>
                <HStack justify="center" w="100%">
                  <Button
                    size="sm"
                    onClick={() => setPointsToWin(Math.max(1, pointsToWin - 1))}
                    _hover={{ bg: '#3182ce' }}
                    bg="#191919"
                    color="white"
                    borderRadius="2px"
                    minW="60px"
                    border="1px solid"
                    borderColor="gray.600"
                  >
                    -
                  </Button>
                  <Text color="white" fontSize="md" minW="120px" textAlign="center">
                    {pointsToWin} Points
                  </Text>
                  <Button
                    size="sm"
                    onClick={() => setPointsToWin(Math.min(100, pointsToWin + 1))}
                    _hover={{ bg: '#3182ce' }}
                    bg="#191919"
                    color="white"
                    borderRadius="2px"
                    minW="60px"
                    border="1px solid"
                    borderColor="gray.600"
                  >
                    +
                  </Button>
                </HStack>
              </VStack>
            </FormControl>

            <FormControl mb={8}>
              <FormLabel textAlign="left">Points structure</FormLabel>
              <HStack justify="center" spacing={4}>
                <Button
                  colorScheme={winByTwo === true ? 'blue' : 'gray'}
                  onClick={() => setWinByTwo(true)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={winByTwo === true ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  Win by 2
                </Button>
                <Button
                  colorScheme={winByTwo === false ? 'blue' : 'gray'}
                  onClick={() => setWinByTwo(false)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={winByTwo === false ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  Golden
                </Button>
                <Button
                  colorScheme={winByTwo === null ? 'blue' : 'gray'}
                  onClick={() => setWinByTwo(null)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={winByTwo === null ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  Fixed
                </Button>
              </HStack>
            </FormControl>
          </VStack>
        </Collapse>

        <Collapse in={scoringStructure === 'tennis'}>
          <VStack spacing={4} w="100%">
            <FormControl>
              <FormLabel>Best of</FormLabel>
              <HStack spacing={2}>
                <Button
                  colorScheme={bestOfSets === 1 ? 'blue' : 'gray'}
                  onClick={() => setBestOfSets(1)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={bestOfSets === 1 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  1 Set
                </Button>
                <Button
                  colorScheme={bestOfSets === 3 ? 'blue' : 'gray'}
                  onClick={() => setBestOfSets(3)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={bestOfSets === 3 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  3 Sets
                </Button>
                <Button
                  colorScheme={bestOfSets === 5 ? 'blue' : 'gray'}
                  onClick={() => setBestOfSets(5)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={bestOfSets === 5 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  5 Sets
                </Button>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>Games per set</FormLabel>
              <HStack justify="center" spacing={4}>
                <Button
                  colorScheme={gamesPerSet === 4 ? 'blue' : 'gray'}
                  onClick={() => setGamesPerSet(4)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={gamesPerSet === 4 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  4 Games
                </Button>
                <Button
                  colorScheme={gamesPerSet === 5 ? 'blue' : 'gray'}
                  onClick={() => setGamesPerSet(5)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={gamesPerSet === 5 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  5 Games
                </Button>
                <Button
                  colorScheme={gamesPerSet === 6 ? 'blue' : 'gray'}
                  onClick={() => setGamesPerSet(6)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={gamesPerSet === 6 ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  6 Games
                </Button>
              </HStack>
            </FormControl>

            <FormControl>
              <FormLabel>Set tie-breaker</FormLabel>
              <HStack justify="center">
                <Button
                  onClick={() => {
                    const options = ['firstTo7', 'superTie10'];
                    let idx = options.indexOf(tiebreakerType);
                    let nextIdx = (idx + 1) % options.length;
                    setTiebreakerType(options[nextIdx]);
                  }}
                  bg="#191919"
                  color="white"
                  w="302.4px"
                  fontWeight="normal"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  textAlign="left"
                  _hover={{ bg: '#2a2a2a' }}
                  border="1px solid"
                  borderColor="gray.600"
                  borderRadius="2px"
                  rightIcon={undefined}
                >
                  <span>
                    {tiebreakerType === 'firstTo7' ? '7 points' : '10 points (as 3rd set)'}
                  </span>
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                    <FaSync size={16} color="#A0AEC0" />
                  </span>
                </Button>
              </HStack>
            </FormControl>

            <FormControl mb={8}>
              <FormLabel>Match structure</FormLabel>
              <HStack justify="center" spacing={4}>
                <Button
                  colorScheme={winByTwo === true ? 'blue' : 'gray'}
                  onClick={() => setWinByTwo(true)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={winByTwo === true ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  Win by 2
                </Button>
                <Button
                  colorScheme={winByTwo === false ? 'blue' : 'gray'}
                  onClick={() => setWinByTwo(false)}
                  flex={1}
                  _hover={{ bg: '#3182ce' }}
                  bg={winByTwo === false ? '#3182ce' : undefined}
                  borderRadius="2px"
                >
                  Golden Point
                </Button>
              </HStack>
            </FormControl>
          </VStack>
        </Collapse>

        <VStack spacing={4} w="100%">
          <FormControl>
            <FormLabel textAlign="center">Player/Team (Serving 1st)</FormLabel>
            <HStack justify="center">
              <Button
                onClick={() => {
                  if (moreColorsEnabled) {
                    const colors: TeamColor[] = ['yellow', 'orange', 'purple', 'blue', 'green', 'red'];
                    let idx = colors.indexOf(team1Color);
                    let nextIdx = (idx + 1) % colors.length;
                    if (colors[nextIdx] === team2Color) {
                      nextIdx = (nextIdx + 1) % colors.length;
                    }
                    setTeam1Color(colors[nextIdx]);
                  }
                }}
                bg="#191919"
                color="white"
                w="302.4px"
                fontWeight="normal"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                textAlign="left"
                _hover={{ bg: moreColorsEnabled ? '#2a2a2a' : '#191919' }}
                border="1px solid"
                borderColor="gray.600"
                borderRadius="2px"
                cursor={moreColorsEnabled ? "pointer" : "default"}
              >
                <span>
                  {(() => {
                    const colorLabels: Record<TeamColor, string> = {
                      red: 'Red / Romeo Point',
                      yellow: 'Yellow / Yankee Point',
                      green: 'Green / Golf Point',
                      purple: 'Purple / Charlie Point',
                      blue: 'Blue / Bravo Point',
                      orange: 'Orange / Oscar Point',
                    };
                    return colorLabels[team1Color];
                  })()}
                </span>
                <span style={{ display: moreColorsEnabled ? 'flex' : 'none', alignItems: 'center' }}>
                  <FaSync size={16} color="#A0AEC0" />
                </span>
              </Button>
            </HStack>
          </FormControl>

          <FormControl>
            <HStack justify="center">
              <Input 
                value={team1Name} 
                onChange={(e) => setTeam1Name(e.target.value.slice(0, 14))}
                placeholder="Display name (optional)"
                bg="#191919"
                color="white"
                maxW="302.4px"
                _placeholder={{ color: 'gray.400' }}
                fontWeight="normal"
                borderRadius="2px"
                border="1px solid"
                borderColor="gray.600"
              />
            </HStack>
          </FormControl>

          <FormControl>
            <FormLabel textAlign="center">Player/Team (Receiving)</FormLabel>
            <HStack justify="center">
              <Button
                onClick={() => {
                  const colors: TeamColor[] = ['yellow', 'orange', 'purple', 'blue', 'green', 'red'];
                  let idx = colors.indexOf(team2Color);
                  let nextIdx = (idx + 1) % colors.length;
                  if (colors[nextIdx] === team1Color) {
                    nextIdx = (nextIdx + 1) % colors.length;
                  }
                  setTeam2Color(colors[nextIdx]);
                }}
                bg="#191919"
                color="white"
                w="302.4px"
                fontWeight="normal"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                textAlign="left"
                _hover={{ bg: '#2a2a2a' }}
                border="1px solid"
                borderColor="gray.600"
                borderRadius="2px"
              >
                <span>
                  {(() => {
                    const colorLabels: Record<TeamColor, string> = {
                      red: 'Red / Romeo Point',
                      yellow: 'Yellow / Yankee Point',
                      green: 'Green / Golf Point',
                      purple: 'Purple / Charlie Point',
                      blue: 'Blue / Bravo Point',
                      orange: 'Orange / Oscar Point',
                    };
                    return colorLabels[team2Color];
                  })()}
                </span>
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                  <FaSync size={16} color="#A0AEC0" />
                </span>
              </Button>
            </HStack>
          </FormControl>

          <FormControl>
            <HStack justify="center">
              <Input 
                value={team2Name} 
                onChange={(e) => setTeam2Name(e.target.value.slice(0, 14))}
                placeholder="Display name (optional)"
                bg="#191919"
                color="white"
                maxW="302.4px"
                _placeholder={{ color: 'gray.400' }}
                fontWeight="normal"
                borderRadius="2px"
                border="1px solid"
                borderColor="gray.600"
              />
            </HStack>
          </FormControl>
        </VStack>

        {/* Club QR code toggle (Phase 6 logic) */}
        <VStack spacing={4} align="center" mt={8} pb={24}>
          <FormControl display="flex" alignItems="center">
            <Tooltip 
              label="Enable additional team color options beyond the standard set"
              placement="top"
              hasArrow
              bg="gray.800"
              color="white"
              borderRadius="md"
              p={3}
              maxW="300px"
            >
              <FormLabel mb="0" mr={0} cursor="help">More team colours (Click only)</FormLabel>
            </Tooltip>
            <Popover trigger="click">
              <PopoverTrigger>
                <IconButton
                  aria-label="Info"
                  icon={<InfoIcon />}
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  mx={0.5}
                  _hover={{ color: 'gray.200' }}
                />
              </PopoverTrigger>
              <PopoverContent bg="gray.800" color="white" borderColor="gray.600" maxW="300px">
                <PopoverArrow bg="gray.800" />
                <PopoverBody p={3}>
                  The AI voice recognition works best with Yellow, Orange & Purple. Other colours are recommended for click scoring only.
                </PopoverBody>
              </PopoverContent>
            </Popover>
            <Switch
              isChecked={moreColorsEnabled}
              onChange={(e) => setMoreColorsEnabled(e.target.checked)}
              colorScheme="blue"
            />
          </FormControl>

          <FormControl display="flex" alignItems="center">
            <Tooltip 
              label="For club use, this displays a QR Code directly on the scoring screen so users can scan and connect, to control the scoring display easily."
              placement="top"
              hasArrow
              bg="gray.800"
              color="white"
              borderRadius="md"
              p={3}
              maxW="300px"
            >
              <FormLabel mb="0" cursor="help">Display Controller QR Code (Club)</FormLabel>
            </Tooltip>
            <Switch
              isChecked={!clubQRCodeDisabled && showQRCode}
              onChange={clubQRCodeDisabled || clubQRCodeLoading ? undefined : (e) => setShowQRCode(e.target.checked)}
              colorScheme="blue"
              isDisabled={clubQRCodeDisabled || clubQRCodeLoading}
            />
          </FormControl>
        </VStack>
        
        {/* Fixed bottom bar for Start/Resume Match (Phase 6) */}
        <Box position="fixed" bottom={0} left={0} right={0} bg="#3182ce" borderTop="1px solid" borderColor="gray.700" p={2} zIndex={1000}>
          <VStack spacing={3} align="center">
            <Button
              type="submit"
              size="lg"
              width="200px"
              bg="#09090b"
              color="#3182ce"
              border="1px solid"
              borderColor="#3182ce"
              isLoading={isLoading}
              borderRadius="2px"
              _hover={{ bg: '#191919' }}
            >
              Start New Match
            </Button>
            {recentMatchId && (
              <Button
                onClick={handleResumeMatch}
                colorScheme="blue"
                variant="outline"
                size="lg"
                width="200px"
                borderRadius="2px"
              >
                Resume Match
              </Button>
            )}
          </VStack>
        </Box>
      </VStack>
      {/* Start Match Popup (Phase 6 color improvements) */}
      <ScaleFade in={showStartMatchPopup} initialScale={0.9} unmountOnExit>
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(0, 0, 0, 0.95)"
          zIndex={2000}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack spacing={8} textAlign="center" p={8} maxW="600px">
            <VStack spacing={4} textAlign="center" w="100%">
              <Text fontSize="lg" fontWeight="bold" color="white">
                Allow microphone use
              </Text>
              <Text fontSize="lg" color="#3182ce" lineHeight="1.6">
                For the best experience use a wire-less mic
              </Text>
              <Text fontSize="lg" fontWeight="bold" color="white" mt={4}>
                Mute chimes
              </Text>
              <Text fontSize="lg" color="#3182ce" lineHeight="1.6">
                Go to sound settings and turn down/off "Notifications" <Text as="span" fontSize="xs" color="gray.500" fontWeight="normal">🔔</Text> volume. This will not affect Ringtone and Message notifications.
              </Text>
            </VStack>
            <Button
              onClick={handleGotItClick}
              bg="#3182ce"
              color="white"
              border="1px solid"
              borderColor="#3182ce"
              variant="outline"
              size="lg"
              width="200px"
              mt={4}
              borderRadius="2px"
            >
              Got it!
            </Button>
            <Button
              variant="link"
              color="gray.400"
              fontSize="md"
              textDecoration="underline"
              mt={1}
              _hover={{ color: 'gray.200', textDecoration: 'underline' }}
              onClick={handleNeverTellMeAgain}
              borderRadius="2px"
            >
              Never tell me again
            </Button>
          </VStack>
        </Box>
      </ScaleFade>

      {/* Court Selection Modal */}
      <CourtSelectionModal
        isOpen={showCourtSelection}
        onClose={() => {
          setShowCourtSelection(false)
          setPendingMatchData(null)
          setIsLoading(false)
        }}
        onCourtSelected={handleCourtSelected}
        maxCourts={userSubscription?.courts || 1}
        userEmail={auth.currentUser?.email || ''}
      />
    </Box>
  )
} 